import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { User, Settings, LogOut, Target, Compass, Landmark } from 'lucide-react';
import { APP_NAME } from '@/lib/branding';

export default function Header() {
  const { pathname } = useLocation();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const isHome = pathname === '/';
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (!isHome) return;
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHome]);
  
  const current = (p: string) => pathname === p ? { 'aria-current': 'page' as const } : {};

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch {
      // no-op (still navigate)
    } finally {
      navigate('/auth/signin', { replace: true });
    }
  };

  // Default (non-home or scrolled) styles
  let headerStyle: React.CSSProperties = {};
  let headerClass = 'sticky top-0 z-[110] backdrop-blur-md bg-background/80 border-b border-border/50';

  if (isHome) {
    headerClass = 'z-[110]';
    headerStyle = {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      transition: 'background 0.4s ease, backdrop-filter 0.4s ease, box-shadow 0.4s ease',
      background: scrolled ? 'rgba(247, 244, 240, 0.88)' : 'transparent',
      backdropFilter: scrolled ? 'blur(14px)' : 'none',
      WebkitBackdropFilter: scrolled ? 'blur(14px)' : 'none',
      boxShadow: scrolled ? '0 1px 0 rgba(0,0,0,0.06)' : 'none',
    };
  }

  return (
    <header className={headerClass} style={headerStyle}>
      <div className="flex items-center justify-between px-4 py-3 max-w-6xl mx-auto">
        <Link 
          to="/" 
          className="font-semibold text-lg text-foreground hover:text-primary transition-colors"
        >
          {APP_NAME}
        </Link>
        
        <nav className="flex items-center gap-1 sm:gap-2" aria-label="Primary">
          {user ? (
            <>
              <Link 
                to="/dashboard" 
                {...current('/dashboard')}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-full hover:bg-muted"
              >
                <User className="h-4 w-4" />
                <span className="hidden sm:inline">Dashboard</span>
              </Link>

              <Link 
                to="/goals" 
                {...current('/goals')}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-full hover:bg-muted"
              >
                <Target className="h-4 w-4" />
                <span className="hidden sm:inline">Goals</span>
              </Link>

              <Link 
                to="/debt" 
                {...current('/debt')}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-full hover:bg-muted"
              >
                <Landmark className="h-4 w-4" />
                <span className="hidden sm:inline">Debt</span>
              </Link>

              <Link 
                to="/future-me" 
                {...current('/future-me')}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-full hover:bg-muted"
              >
                <Compass className="h-4 w-4" />
                <span className="hidden sm:inline">Future Me</span>
              </Link>
              
              <Link 
                to="/settings/profile" 
                {...current('/settings/profile')}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-full hover:bg-muted"
              >
                <Settings className="h-4 w-4" />
                <span className="hidden sm:inline">Settings</span>
              </Link>
              
              <button 
                onClick={handleSignOut}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-full hover:bg-muted ml-1"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Sign out</span>
              </button>
            </>
          ) : (
            <>
              <Link 
                to="/blog" 
                {...current('/blog')}
                className="px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-full"
              >
                Blog
              </Link>
              <Link 
                to="/auth/signin" 
                {...current('/auth/signin')}
                className="px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-full"
              >
                Sign in
              </Link>
              <Link 
                to="/quiz" 
                className="px-5 py-2 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors rounded-full"
              >
                Start Free
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
